import { useCallback, useContext, useEffect } from "react";
import useAnalyticsContext from "./analytics-context/useAnalyticsContext";
import { useLocation } from "react-router-dom";
import { User } from "@/model/User";
import { OrganizationModel } from "@/model/Organization";
import {
    AuditLinkOpenedEvent,
    AuditResultOpenedEvent,
    TrackableEventWrapper,
    Integration,
    RoadmapFeature,
    UpgradeDialogOpenedEvent,
    SetupGuideStepCompletedEvent,
    ChatQuestionAskedEvent,
    ChatRefusedEvent,
    ChatAllowanceActionClickedEvent,
    ChatAnswerRatedEvent,
} from "@/types/generated/management-api-types";
import TrackableEventFactory from "./trackableEventFactory";
import UserContext from "../../app/UserContext";
import { getCookie } from "@/util/cookie.ts";
type SetupGuideStep = SetupGuideStepCompletedEvent["step"];

let hasTrackedLogin = false;
const isProd = import.meta.env.PROD;

const useAnalytics = () => {
    const { analytics } = useAnalyticsContext();

    if (isProd) {
        const anonymousId = getCookie("sp__anon_id");
        if (anonymousId) {
            analytics?.setAnonymousId(anonymousId);
        }
    }

    const user = useContext(UserContext);

    const identifyUser = useCallback(
        (user: User, website?: string) => {
            if (
                user.isManager() &&
                user.currentOrganization.id !== user.managementOrganization?.id
            ) {
                return;
            }

            const nameParts = user.name.split(" ");
            const firstName = nameParts.shift();
            const lastName = nameParts.join(" ");
            analytics?.identify(user.id, {
                name: user.name,
                firstName,
                lastName,
                email: user.email,
            });

            analytics?.group(user.currentOrganization.id, {
                plan: user.currentOrganization.plan,
                website,
            });
        },
        [analytics],
    );

    const usePageViews = () => {
        const location = useLocation();
        useEffect(() => {
            analytics?.page();
        }, [location.pathname]);
    };

    const trackEvent = (event: TrackableEventWrapper) => {
        analytics?.track(event.event, event.properties);
    };

    const page = () => {
        analytics?.page();
    };

    const trackErrorEncounteredEvent = (message: string) => {
        if (!user) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createErrorEncounteredEvent(
                { message },
                user.currentOrganization,
            ),
        );
    };

    const trackUpgradeDialogOpenedEvent = (
        opener: UpgradeDialogOpenedEvent["opener"],
    ) => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createUpgradeDialogOpenedEvent(
                { opener },
                user.currentOrganization,
            ),
        );
    };

    const trackSignOutEvent = () => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createUserSignedOutEvent(
                { user },
                user.currentOrganization,
            ),
        );
    };

    const trackUserUpgradedEvent = () => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createUserUpgradedEvent(
                { user },
                user.currentOrganization,
            ),
        );
    };

    const trackExpressedIntegrationInterestEvent = (
        integration: Integration | "lens" | "webhook",
        isForAutomations?: boolean,
    ) => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createExpressedIntegrationInterestEvent(
                // @ts-expect-error Lens can be an integration for now
                { user, integration, isForAutomations },
                user.currentOrganization,
            ),
        );
    };

    const trackSuggestedIntegrationEvent = (integration: string) => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createSuggestedIntegrationEvent(
                { user, integration },
                user.currentOrganization,
            ),
        );
    };

    const trackExpressedRoadmapFeatureInterestEvent = (
        roadmapFeature: RoadmapFeature,
    ) => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createExpressedRoadmapFeatureInterest(
                { user, roadmapFeature },
                user.currentOrganization,
            ),
        );
    };

    const trackSetupGuideOpenedEvent = () => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createSetupGuideOpenedEvent(
                {},
                user.currentOrganization,
            ),
        );
    };

    const trackSetupGuideStepCompletedEvent = (step: SetupGuideStep) => {
        if (!user?.currentOrganization) {
            return;
        }
        trackEvent(
            TrackableEventFactory.createSetupGuideStepCompletedEvent(
                { step },
                user.currentOrganization,
            ),
        );
    };

    // Chat. None of these carries a question, an answer or a comment: what
    // was asked stays in the transcript, where deletion reaches it.
    //
    // Stable across renders, unlike the functions above: chat's send handlers
    // depend on them, and a new function every render would rebuild those on
    // every streamed chunk.
    const organization = user?.currentOrganization;
    type ChatOrganization = NonNullable<typeof organization>;
    const trackChat = useCallback(
        (build: (organization: ChatOrganization) => TrackableEventWrapper) => {
            if (!organization) {
                return;
            }
            const event = build(organization);
            analytics?.track(event.event, event.properties);
        },
        [analytics, organization],
    );

    const trackChatOpenedEvent = useCallback(
        () =>
            trackChat((organization) =>
                TrackableEventFactory.createChatOpenedEvent({}, organization),
            ),
        [trackChat],
    );

    const trackChatQuestionAskedEvent = useCallback(
        (source: ChatQuestionAskedEvent["source"]) =>
            trackChat((organization) =>
                TrackableEventFactory.createChatQuestionAskedEvent(
                    { source },
                    organization,
                ),
            ),
        [trackChat],
    );

    const trackChatRefusedEvent = useCallback(
        (reason: ChatRefusedEvent["reason"]) =>
            trackChat((organization) =>
                TrackableEventFactory.createChatRefusedEvent(
                    { reason },
                    organization,
                ),
            ),
        [trackChat],
    );

    const trackChatAllowanceActionClickedEvent = useCallback(
        (
            action: ChatAllowanceActionClickedEvent["action"],
            state: ChatAllowanceActionClickedEvent["state"],
        ) =>
            trackChat((organization) =>
                TrackableEventFactory.createChatAllowanceActionClickedEvent(
                    { action, state },
                    organization,
                ),
            ),
        [trackChat],
    );

    const trackChatAnswerRatedEvent = useCallback(
        (rating: ChatAnswerRatedEvent["rating"]) =>
            trackChat((organization) =>
                TrackableEventFactory.createChatAnswerRatedEvent(
                    { rating },
                    organization,
                ),
            ),
        [trackChat],
    );

    // The Icp audit (specs/icp-fit/outbound-audit.md §8). Stable across
    // renders: screens fire these from effects.
    //
    // Before the sign-up there is no user and no group: the link's page sends
    // the prospect's website under Segment's anonymous id, which the identify
    // at sign-up ties to the account. The token is the credential and never
    // leaves the page.
    const trackAuditLinkOpenedEvent = useCallback(
        (properties: Pick<AuditLinkOpenedEvent, "domain">) =>
            analytics?.track("Audit Link Opened", properties),
        [analytics],
    );

    const trackAuditResultOpenedEvent = useCallback(
        (result: AuditResultOpenedEvent["result"]) =>
            trackChat((organization) =>
                TrackableEventFactory.createAuditResultOpenedEvent(
                    { result },
                    organization,
                ),
            ),
        [trackChat],
    );

    const trackAuditListDownloadedEvent = useCallback(
        () =>
            trackChat((organization) =>
                TrackableEventFactory.createAuditListDownloadedEvent(
                    {},
                    organization,
                ),
            ),
        [trackChat],
    );

    const useIdentifiedUser = (
        userModel: User,
        organization?: OrganizationModel,
        website?: string,
    ) => {
        useEffect(() => {
            if (!userModel || !organization || hasTrackedLogin) {
                return;
            }

            identifyUser(userModel, website);

            hasTrackedLogin = true;
        }, [organization, userModel]);
    };

    const reset = () => {
        hasTrackedLogin = false;
        analytics?.reset();
    };

    return {
        reset,
        identifyUser,
        page,
        usePageViews,
        useIdentifiedUser,
        trackErrorEncounteredEvent,
        trackSignOutEvent,
        trackUserUpgradedEvent,
        trackUpgradeDialogOpenedEvent,
        trackExpressedIntegrationInterestEvent,
        trackSuggestedIntegrationEvent,
        trackExpressedRoadmapFeatureInterestEvent,
        trackSetupGuideOpenedEvent,
        trackSetupGuideStepCompletedEvent,
        trackChatOpenedEvent,
        trackChatQuestionAskedEvent,
        trackChatRefusedEvent,
        trackChatAllowanceActionClickedEvent,
        trackChatAnswerRatedEvent,
        trackAuditLinkOpenedEvent,
        trackAuditResultOpenedEvent,
        trackAuditListDownloadedEvent,
    };
};

export default useAnalytics;
